import React from 'react';
import SEO from './SEO';
import AppStoreBadge from './AppStoreBadge';
import Icon from './Icon';

const LoanComparison = () => {
  const loanComparisonStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Compare Mortgage Loans & Best Mortgage Lenders",
      "url": "https://www.lown.app/loan-comparison",
      "description": "Use Lown's free, ad-free mortgage loan comparison tool to evaluate best mortgage lenders, estimate how much house you can afford, and find homes in eco-friendly, walkable neighborhoods.",
      "inLanguage": "en-US",
      "about": [
        { "@type": "Thing", "name": "Compare mortgage loans" },
        { "@type": "Thing", "name": "Best mortgage lenders" },
        { "@type": "Thing", "name": "How much house can I afford" }
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.lown.app/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Loan Comparison",
            "item": "https://www.lown.app/loan-comparison"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Compare Mortgage Loans & Lenders",
      "description": "Step-by-step guide for comparing mortgage loans, rates, and lender offers with Lown's free mortgage calculator.",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": "0"
      },
      "step": [
        {
          "@type": "HowToStep",
          "name": "Gather mortgage offers",
          "text": "Collect quotes from at least three best mortgage lenders, including local credit unions and online lenders."
        },
        {
          "@type": "HowToStep",
          "name": "Compare interest rates and APR",
          "text": "Enter each loan offer in Lown to compare rates, APR, closing costs, and monthly payments."
        },
        {
          "@type": "HowToStep",
          "name": "Evaluate affordability and neighborhoods",
          "text": "Use Lown's calculators to see how much house you can afford and filter for eco-friendly homes or walkable neighborhoods."
        },
        {
          "@type": "HowToStep",
          "name": "Review financing programs",
          "text": "Add FHA down payment assistance, USDA loan eligibility, or VA loan requirements to see how each program affects affordability."
        }
      ],
      "supply": [
        {
          "@type": "HowToSupply",
          "name": "Mortgage lender estimates"
        },
        {
          "@type": "HowToSupply",
          "name": "Lown mortgage calculator app"
        }
      ],
      "tool": [
        {
          "@type": "HowToTool",
          "name": "Loan comparison tool"
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Compare Mortgage Loans & Best Mortgage Lenders Side by Side"
        description="Compare mortgage loans from the best mortgage lenders, calculate how much house you can afford, and explore eco-friendly, walkable neighborhoods with Lown's free, ad-free loan comparison tool."
        keywords="compare mortgage loans, best mortgage lenders, how much house can I afford, houses for sale near me, eco-friendly homes near me, walkable neighborhoods, FHA down payment assistance, USDA loan eligibility, VA loan requirements"
        url="https://www.lown.app/loan-comparison"
        type="article"
        image="https://www.lown.app/lown_homescreen.png"
        structuredData={loanComparisonStructuredData}
      />
      
      <div className="loan-comparison-page">
        <div className="page-header">
          <h1>Compare Mortgage Loans & Best Mortgage Lenders Side by Side</h1>
          <p className="page-subtitle">Make informed decisions by comparing multiple mortgage options with Lown's loan comparison tool to see payments, affordability, and the best houses for sale near me in walkable neighborhoods.</p>
        </div>

        <div className="comparison-features animate-fade-in-up">
          <div className="feature-grid">
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="chart" />
              </div>
              <h3>Side-by-Side Analysis</h3>
              <p>Compare up to 4 different mortgage offers simultaneously with detailed breakdowns of rates, terms, and costs.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="wallet" />
              </div>
              <h3>Total Cost Comparison</h3>
              <p>See the true cost of each loan including interest, fees, and closing costs over the entire loan term.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="chart" />
              </div>
              <h3>Payment Schedule Analysis</h3>
              <p>Compare monthly payments, principal vs. interest breakdowns, and amortization schedules.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="target" />
              </div>
              <h3>Payoff Timeline Comparison</h3>
              <p>See how different loan terms and rates affect your payoff timeline and total interest paid.</p>
            </div>
          </div>
        </div>

        <div className="coming-soon-section animate-fade-in-up">
          <div className="coming-soon-content">
            <h2>Coming Soon in the Lown App</h2>
            <p>Our loan comparison tool is currently in development and will be available in the Lown app soon. This powerful feature will help you:</p>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Compare multiple lender offers side by side</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Analyze total costs including fees and closing costs</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>See detailed payment schedules and amortization</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Calculate savings from different loan terms</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Export comparison reports for lender discussions</span>
              </div>
            </div>
            
            <div className="cta-section animate-fade-in-up">
              <AppStoreBadge
                className="comparison-app-store-cta"
                ariaLabel="Get the Lown app on the App Store"
              />
              <p className="cta-note">Be the first to try our loan comparison tool when it launches</p>
            </div>
          </div>
        </div>

        <div className="educational-content animate-fade-in-up">
          <h2>How to Compare Mortgage Loans</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Gather Multiple Offers</h3>
                <p>Get pre-approval letters from at least 3-4 different lenders to have options to compare.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Compare Interest Rates</h3>
                <p>Look at both the interest rate and APR (Annual Percentage Rate) which includes fees.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Analyze Loan Terms</h3>
                <p>Compare loan length (15 vs 30 years), loan type (fixed vs adjustable), and down payment requirements.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Calculate Total Costs</h3>
                <p>Include closing costs, origination fees, and any points to see the true cost of each loan.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="faq-section animate-fade-in-up">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-container">
            <div className="faq-item">
              <h3>What's the difference between interest rate and APR?</h3>
              <p>The interest rate is the cost of borrowing money, while APR includes the interest rate plus other fees like origination fees, closing costs, and points. APR gives you a more complete picture of the loan's true cost.</p>
            </div>
            
            <div className="faq-item">
              <h3>Should I choose a 15-year or 30-year mortgage?</h3>
              <p>15-year mortgages have higher monthly payments but lower total interest costs. 30-year mortgages have lower monthly payments but higher total interest. Choose based on your budget and financial goals.</p>
            </div>
            
            <div className="faq-item">
              <h3>What are mortgage points and should I pay them?</h3>
              <p>Points are fees paid upfront to reduce your interest rate. Each point typically costs 1% of your loan amount and reduces your rate by 0.25%. Pay points if you plan to stay in the home long enough to recoup the upfront cost.</p>
            </div>
            
            <div className="faq-item">
              <h3>How do I know if I'm getting a good deal?</h3>
              <p>Compare multiple offers and look at the APR, total closing costs, and loan terms. A good deal depends on your specific situation, but generally lower APRs and reasonable closing costs indicate competitive offers.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanComparison;
